import { useParams, useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import { QueueListIcon } from '@heroicons/react/24/outline'
import { CheckCircle } from 'iconoir-react'
import moment from 'moment'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { matchPath } from 'react-router-dom'
import { DateComponent } from 'app/components/DateComponent'
import { LazyLocationCell, getName } from 'app/components/LazyLocationCell'
import PhoneNumber from 'app/components/PhoneNumber'
import { DateRangeOperator, FilterValueType, FilterableColumn } from 'app/filter.utils'
import { CallJson } from 'app/types/entities'
import { LocationType } from 'app/types/entities'
import { debug, logError } from 'lib/logging'
import React from 'react'
import { useCallStore } from 'app/stores/StoreContext'

export type CallsLoaderData = {
  calls: Promise<{ hits: CallJson[]; total: number }>
  location?: LocationType
  focusedCall?: CallJson
  slug: string
}

export type UseCallsReturn = {
  calls: { hits: CallJson[]; total: number } | undefined
  columns: ColumnDef<CallJson, any>[]
  filterableColumns: FilterableColumn[]
  slug: string
  isLoading: boolean
  openDrawer: boolean
  focusedCall: CallJson | undefined
  location: LocationType | undefined
  updatedCalls: CallJson[]
  onCallSelected: (call: CallJson) => Promise<void>
  onCloseDrawer: () => void
  onCallUpdate: (call: CallJson) => void
}

const getColumns = (
  onCallSelected: (call: CallJson) => void,
  updatedCalls?: CallJson[],
): ColumnDef<CallJson, any>[] => [
  {
    id: `caller Id Name`,
    header: `Caller ID: Name`,
    accessorFn: (row) => row.callerId,
    enableResizing: true,
    enableHiding: false,
    minSize: 100,
    cell: (props) =>
      React.createElement(
        'button',
        {
          className: classNames(`cursor-pointer capitalize hover:font-bold`),
          onClick: () => onCallSelected(props.row.original),
        },
        props.getValue()?.toLocaleLowerCase(),
      ),
    meta: {
      defaultVisible: true,
      pinned: true,
      sortKey: `callerId.keyword`,
    },
  },
  {
    id: `createdDate`,
    header: `Created Date`,
    accessorFn: (row) => row.callDate,
    cell: (props) => React.createElement(DateComponent, { value: props.getValue(), dateOnly: true }),
    enableHiding: false,
    meta: { defaultVisible: true, sortKey: `createdDate` },
  },
  {
    id: `startTime`,
    header: `Start Time`,
    accessorFn: (row) => row.callDate,
    cell: (props) => React.createElement(DateComponent, { value: props.getValue(), format: 'h:mm A' }),
    meta: { defaultVisible: true },
    enableSorting: false,
  },
  {
    id: `locationId`,
    accessorKey: `locationId`,
    header: `Location`,
    cell: ({ row }) => React.createElement(LazyLocationCell, { locationId: row.original.locationId! }),
    meta: { defaultVisible: true },
    enableSorting: false,
  },
  {
    id: `callerPhoneNumber`,
    header: `Caller ID: Phone Number`,
    accessorFn: (row) => row.callerPhone,
    cell: (props) => React.createElement(PhoneNumber, { number: props.getValue() }),
    meta: { defaultVisible: true, sortKey: `callerPhone.keyword` },
  },
  {
    id: `duration`,
    header: `Duration`,
    accessorFn: (row) => {
      let duration = row.recordingDuration ?? 0
      let minutes = Math.floor(duration / 60)
      let seconds = duration % 60
      return `${minutes}m ${seconds.toString().padStart(2, `0`)}s`
    },
    meta: { defaultVisible: true, sortKey: `recordingDuration` },
  },
  {
    id: `status`,
    accessorFn: (row) => ({ tags: row.tags, status: row.status, id: row.id }),
    cell: (props) => {
      let data = props.getValue()
      let status: string | undefined = undefined
      let updatedCall = updatedCalls?.find((call) => call.id === data.id)
      let getCallStatus = (callStatus: string | undefined, tags: string[] | undefined) => {
        if (callStatus === `Complete`) {
          status = `Complete`
        } else if (tags?.find((tag: string) => tag.toLowerCase() === `potential lead`)) {
          status = `Queue`
        }
      }
      if (updatedCall) {
        getCallStatus(updatedCall.status, updatedCall.tags)
      } else {
        getCallStatus(data.status, data.tags)
      }
      return status
        ? React.createElement(
            'div',
            {
              className: classNames(
                { [`bg-surface-muted shadow-sm`]: status === `Queue` },
                `inline-flex items-center justify-start gap-x-1 rounded border px-2 py-0.5 text-body-sm text-strong`,
              ),
            },
            status === `Queue` &&
              React.createElement(QueueListIcon, {
                width: 16,
                height: 16,
                strokeWidth: 1.5,
                className: 'text-strong',
              }),
            status === `Complete` &&
              React.createElement(CheckCircle, {
                width: 16,
                height: 16,
                strokeWidth: 1.5,
                className: 'text-green-500',
              }),
            status,
          )
        : null
    },
    header: `Status`,
    meta: { sortKey: `status.keyword` },
  },
]

const buildFilterableColumns = (): FilterableColumn[] => [
  {
    attribute: `createdDate`,
    displayName: `Created Date`,
    type: FilterValueType.rangeDate,
    dateOperator: DateRangeOperator.this_month,
    defaultValue: [moment().startOf(`month`).format(`YYYY-MM-DD`), moment().endOf(`month`).format(`YYYY-MM-DD`)],
    operator: `between`,
  },
  {
    attribute: `locationId`,
    displayName: `Location`,
    type: FilterValueType.id,
    optionsSource: `/api/locations`,
    uniqOperator: `in`,
  } as FilterableColumn,
  { attribute: `status`, displayName: `Status`, type: FilterValueType.string },
  { attribute: `recordingDuration`, displayName: `Duration`, type: FilterValueType.number },
  { attribute: `callerId`, displayName: `Caller ID: Name`, type: FilterValueType.string },
  { attribute: `callerPhone`, displayName: `Caller ID: Phone Number`, type: FilterValueType.string },
]

export function useCalls(): UseCallsReturn {
  const { slug = `queue` } = useParams()
  const [searchParams] = useSearchParams()
  const callStore = useCallStore()
  const [isLoading, setIsLoading] = useState(false)
  const [openDrawer, setOpenDrawer] = useState<boolean>(false)
  const [updatedCalls, setUpdatedCalls] = useState<CallJson[]>([])
  const [location, setLocation] = useState<LocationType | undefined>()
  const [focusedCall, setFocusedCall] = useState<CallJson | undefined>()

  // Fetch calls via store
  useEffect(() => {
    setIsLoading(true)
    callStore
      .fetchList({
        search: slug !== `queue` ? slug : undefined,
        pagination: { page: Number(searchParams.get(`page`) || 0), limit: 500 },
        sort: undefined,
      })
      .finally(() => setIsLoading(false))
  }, [slug, searchParams.toString()])

  // Load focused call from drawer param
  useEffect(() => {
    const callId = searchParams.get(`drawer`)
    if (callId) {
      callStore
        .fetchOne(callId)
        .then(async (result) => {
          const call = result.data ? ((result.data as any).call ?? result.data) : undefined
          if (call?.id) {
            setFocusedCall(call)
            setOpenDrawer(true)
            if (call.locationId) {
              const locationName = await getName(call.locationId)
              setLocation({ id: call.locationId, name: locationName } as LocationType)
            }
          }
        })
        .catch((err) => logError(`Error fetching call`, err))
    }
  }, [])

  const filterableCols = useMemo(() => buildFilterableColumns(), [slug])

  const onCallSelected = async (call: CallJson) => {
    setFocusedCall(undefined)
    setOpenDrawer(false)
    let locationName = await getName(call.locationId!)
    setLocation({ id: call.locationId, name: locationName } as LocationType)
    history.pushState(null, ``, `/calls/${slug}?drawer=${call.id}`)
    setFocusedCall(call)
    setOpenDrawer(true)
  }

  const columns = useMemo(() => getColumns(onCallSelected, updatedCalls), [updatedCalls])

  const onCloseDrawer = () => {
    setOpenDrawer(false)
    setFocusedCall(undefined)
    searchParams.delete(`drawer`)
    history.pushState(null, ``, `/calls/${slug}`)
  }

  const onCallUpdate = (call: CallJson) => {
    setUpdatedCalls((prev) => [...prev, call])
  }

  useEffect(() => {
    const getSelectedLocation = async (): Promise<LocationType> => {
      if (location) return location
      let locationName = await getName(focusedCall?.locationId!)
      return { name: locationName, id: focusedCall?.locationId } as LocationType
    }
    if (focusedCall?.locationId && !location) {
      let loc = getSelectedLocation()
        .then((res) => res)
        .catch((err) => debug(`call`, `error retrieving location`, err))
      if (loc) {
        setLocation(loc as LocationType)
      }
    }
  }, [focusedCall])

  const calls = callStore.list as { hits: CallJson[]; total: number } | undefined

  return {
    calls,
    columns,
    filterableColumns: filterableCols,
    slug: slug,
    isLoading,
    openDrawer,
    focusedCall,
    location,
    updatedCalls,
    onCallSelected,
    onCloseDrawer,
    onCallUpdate,
  }
}
